/**
 * Template modes (user decision, 25 Oct 2026).
 *
 * A template is written one of two ways:
 *   scratch — the scratch pad: free-form text, images and merge-tag chips under the fixed Amber
 *             header, like writing an email in an inbox. The default for a new template.
 *   blocks  — the guided form (eyebrow, headline, paragraphs, highlight box, button…), which is
 *             how every template before 25 Oct 2026 was written.
 *
 * Pure helpers, no DOM, no React — so they are tested with node --test like the rest of data/.
 */

export const TEMPLATE_MODES = [
  { key: 'scratch', label: 'Scratch pad', icon: 'edit_note', hint: 'Write the mail freely under the Amber logo' },
  { key: 'blocks', label: 'Guided form', icon: 'view_agenda', hint: 'Fill in headline, paragraphs, highlight box, button' },
];

export const NEW_TEMPLATE_MODE = 'scratch';

/** The mode a stored template was written in; anything but "scratch" is the guided form. */
export function modeOf(template) {
  return template?.mode === 'scratch' ? 'scratch' : 'blocks';
}

/** The merge tags a campaign fills in per vendor (mergeParamsFor on the server). */
export const MERGE_PARAMS = [
  { key: 'company_name', label: 'Company name', sample: 'Sample Vendor Pvt Ltd' },
  { key: 'spoc_name', label: 'Contact person', sample: 'Sample Contact' },
  { key: 'vendor_code', label: 'Vendor code', sample: 'VA00000' },
];

export const paramTag = (key) => `{{params.${key}}}`;

export const paramLabel = (key) => MERGE_PARAMS.find((p) => p.key === key)?.label ?? key;

/** `{{params.key}}` / `{{ params.key }}` — the only form the mails use. */
const PARAM_TAG_RE = /\{\{\s*params\.([A-Za-z0-9_]+)\s*\}\}/g;
/** A chip as the editor renders it (any inner label). */
const PARAM_CHIP_RE = /<span\b[^>]*\bdata-param="([A-Za-z0-9_]+)"[^>]*>[\s\S]*?<\/span>/gi;

/**
 * Stored body → editor content: every merge tag becomes a chip so it reads as "Company name"
 * and cannot be half-deleted. Chips already present are normalised, never doubled.
 */
export function withParamChips(html) {
  return String(html ?? '')
    .replace(PARAM_CHIP_RE, (_m, key) => paramTag(key))
    .replace(PARAM_TAG_RE, (_m, key) => `<span data-param="${key}">${paramLabel(key)}</span>`);
}

/** Editor content → what is saved: chips back to their tags, whatever label they showed. */
export function withParamTags(html) {
  return String(html ?? '').replace(PARAM_CHIP_RE, (_m, key) => paramTag(key));
}

/** The browser hands a colour back as rgb(227, 154, 43); the mail wants the #E39A2B it was given. */
export function hexColours(html) {
  return String(html ?? '').replace(/rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/g, (_m, r, g, b) =>
    `#${[r, g, b].map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0')).join('').toUpperCase()}`
  );
}

/** What leaves the editor: chips as tags, colours as hex. */
export function exportHtml(html) {
  return hexColours(withParamTags(html));
}

/** The words of a body, tags stripped — enough to know whether anything was written. */
export function scratchTextOf(html) {
  return String(html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** An empty pad has neither words nor an image. */
export function isScratchEmpty(html) {
  return scratchTextOf(html) === '' && !/<img\b/i.test(String(html ?? ''));
}

/** What the status bar counts: words, images, vendor fields, buttons, tables. */
export function countsOf(html) {
  const s = String(html ?? '');
  const text = scratchTextOf(withParamTags(s).replace(PARAM_TAG_RE, ' '));
  return {
    // A word has a letter or a digit in it; a lone comma left behind by a chip is not one.
    words: text ? text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length : 0,
    images: (s.match(/<img\b/gi) ?? []).length,
    fields: (withParamTags(s).match(PARAM_TAG_RE) ?? []).length,
    buttons: (s.match(/<a\b[^>]*\bdata-button="1"/gi) ?? []).length,
    tables: (s.match(/<table\b/gi) ?? []).length,
  };
}

/** Inbox clients cut a subject around here; the counter turns amber past it. */
export const SUBJECT_SOFT_MAX = 60;

/** Colours an operator may give to text — the mail palette plus a few plain ones. */
export const TEXT_COLORS = [
  { key: 'ink', label: 'Ink', value: '#15263D' },
  { key: 'body', label: 'Body', value: '#3D4A5C' },
  { key: 'muted', label: 'Muted', value: '#7A8798' },
  { key: 'navy', label: 'Navy', value: '#003366' },
  { key: 'amber', label: 'Amber', value: '#E39A2B' },
  { key: 'teal', label: 'Teal', value: '#087F78' },
  { key: 'red', label: 'Red', value: '#B3261E' },
  { key: 'green', label: 'Green', value: '#1E7D4B' },
];

/** Soft highlights that stay readable in an inbox. */
export const HIGHLIGHT_COLORS = [
  { key: 'yellow', label: 'Yellow', value: '#FFF3C4' },
  { key: 'amber', label: 'Amber', value: '#FBE3C2' },
  { key: 'teal', label: 'Teal', value: '#DDF4F0' },
  { key: 'blue', label: 'Blue', value: '#DCE8F7' },
  { key: 'pink', label: 'Pink', value: '#F8E1E7' },
  { key: 'grey', label: 'Grey', value: '#ECEFF2' },
];

/** Text sizes as a mail renders them; "Normal" is the body size and clears the override. */
export const FONT_SIZES = [
  { key: 'small', label: 'Small', px: 13 },
  { key: 'normal', label: 'Normal', px: null },
  { key: 'large', label: 'Large', px: 18 },
  { key: 'xlarge', label: 'Extra large', px: 22 },
];

/** Image widths inside the 520px body column. */
export const IMAGE_SIZES = [
  { key: 's', label: 'Small', width: 160 },
  { key: 'm', label: 'Medium', width: 260 },
  { key: 'l', label: 'Large', width: 390 },
  { key: 'full', label: 'Full width', width: 520 },
];

/** What a new scratch pad opens with — a greeting chip, room to write, the usual sign-off. */
export const STARTER_SCRATCH_HTML =
  '<p>Dear {{params.company_name}},</p><p></p><p>Warm regards,<br><strong>Vendor Management Team</strong><br>Amber Enterprises India Limited</p>';

const trimmed = (v) => String(v ?? '').trim();

/**
 * Exactly the body the API expects for a save or a preview, per mode. Blanks are omitted so
 * server defaults apply; the guided fields never travel with a scratch template and vice versa.
 */
export function templateBodyFor(mode, d) {
  const common = {
    name: trimmed(d.name),
    category: d.category || 'Outreach',
    subject: trimmed(d.subject),
    description: trimmed(d.description) || undefined,
    preview_text: trimmed(d.previewText) || undefined,
    mode,
  };
  if (mode === 'scratch') {
    return { ...common, body_html: withParamTags(d.bodyHtml) };
  }
  const b = {
    ...common,
    eyebrow: trimmed(d.eyebrow) || undefined,
    heading: trimmed(d.heading) || undefined,
    lead: trimmed(d.lead) || undefined,
    paragraphs: parseParagraphs(d.paragraphs),
    callout_title: trimmed(d.calloutTitle) || undefined,
    callout_lines: parseLines(d.calloutLines),
    show_partner_logo: Boolean(d.partnerLogo),
    shared_audience: Boolean(d.sharedAudience),
  };
  if (trimmed(d.ctaLabel) && trimmed(d.ctaUrl)) {
    b.cta_label = trimmed(d.ctaLabel);
    b.cta_url = normalizeUrl(d.ctaUrl);
  }
  if (trimmed(d.signoffName)) b.signoff_name = trimmed(d.signoffName);
  if (trimmed(d.greeting)) b.greeting = trimmed(d.greeting);
  return b;
}

/**
 * The API insists on an absolute URL for the button link, but people type
 * "www.genessence.ai" — give it the scheme instead of failing validation.
 */
export function normalizeUrl(raw) {
  const v = trimmed(raw);
  if (!v) return v;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) || /^mailto:|^tel:/i.test(v) ? v : `https://${v}`;
}

export const parseParagraphs = (t) =>
  String(t ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.trim().replace(/\n+/g, ' '))
    .filter(Boolean);

export const parseLines = (t) =>
  String(t ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/** Browser-side draft recovery: where an unsaved draft of a template is kept. */
export const draftStorageKey = (templateId) => `vendorhub:template-draft:${templateId ?? 'new'}`;

/** Keyboard shortcuts the pad honours (shown in the help popover). */
export const SHORTCUTS = [
  ['Ctrl + S', 'Save'],
  ['Ctrl + B / I / U', 'Bold / italic / underline'],
  ['Ctrl + Shift + X', 'Strikethrough'],
  ['Ctrl + Alt + 1 / 2 / 3', 'Heading 1 / 2 / 3'],
  ['Ctrl + Alt + 0', 'Normal text'],
  ['Ctrl + Shift + 8 / 7', 'Bulleted / numbered list'],
  ['Tab / Shift + Tab', 'Indent / outdent a list item'],
  ['Ctrl + Shift + L / E / R', 'Align left / centre / right'],
  ['Ctrl + Z / Ctrl + Y', 'Undo / redo'],
  ['Ctrl + V with a screenshot', 'Paste an image straight into the mail'],
];
